/**
 * VSCodeAuth.js
 * Authentication page for VS Code extension
 * Users login here and get a token to paste into VS Code
 */

import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import DataService from "../services/DataService";
import { FaCopy, FaCheck } from "react-icons/fa";

export default function VSCodeAuth() {
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [teams, setTeams] = useState([]);

  const state = searchParams.get("state");
  const auth = getAuth();
  const dataService = new DataService();

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await generateToken(currentUser);
        await loadTeams(currentUser);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const generateToken = async (user) => {
    try {
      // Get Firebase ID token
      const idToken = await user.getIdToken();
      const refreshToken = user.refreshToken;

      // Get teams to include in token
      const userTeams = await dataService.getTeams();
      
      // Encode teams as base64 JSON
      const teamsJSON = JSON.stringify(userTeams);
      const teamsBase64 = btoa(teamsJSON);

      // Token format: idToken|refreshToken|userId|email|expiresIn|teamsBase64
      const tokenString = `${idToken}|${refreshToken}|${user.uid}|${user.email}|3600|${teamsBase64}`;
      
      setToken(tokenString);
    } catch (error) {
      console.error("Error generating token:", error);
    }
  };

  const loadTeams = async (user) => {
    try {
      const userTeams = await dataService.getTeams();
      setTeams(userTeams);
    } catch (error) {
      console.error("Error loading teams:", error);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="vscode-auth-page">
        <div className="auth-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="vscode-auth-page">
        <div className="auth-container">
          <h1>🔐 VS Code Extension Authentication</h1>
          <p className="subtitle">
            Please login to authorize the Data-Next VS Code extension
          </p>

          <div className="auth-info">
            <h3>How it works:</h3>
            <ol>
              <li>Login with your Auxo AI account</li>
              <li>Copy the generated token</li>
              <li>Paste it into VS Code when prompted</li>
            </ol>
          </div>

          <p className="instruction">
            Please login using the navigation menu.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="vscode-auth-page">
      <div className="auth-container">
        <div className="success-header">
          <FaCheck size={48} color="#4CAF50" />
          <h1>Authentication Successful!</h1>
        </div>

        <div className="user-info">
          <p>
            <strong>Logged in as:</strong> {user.email}
          </p>
          <p>
            <strong>User ID:</strong> {user.uid}
          </p>
        </div>

        <div className="teams-info">
          <h3>Your Teams ({teams.length}):</h3>
          {teams.length > 0 ? (
            <ul className="teams-list">
              {teams.map((team) => (
                <li key={team.id}>
                  <strong>{team.teamName}</strong>
                  <span className="role-badge">{team.role}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-teams">
              No teams found. Please create a team in the web app first.
            </p>
          )}
        </div>

        <div className="token-section">
          <h3>📋 Copy this token to VS Code:</h3>
          <div className="token-container">
            <textarea 
              className="token-display" 
              value={token} 
              readOnly
              rows={4}
            />
            <button 
              className="copy-button" 
              onClick={copyToken}
              disabled={copied}
            >
              {copied ? (
                <>
                  <FaCheck /> Copied!
                </>
              ) : (
                <>
                  <FaCopy /> Copy Token
                </>
              )}
            </button>
          </div>
        </div>

        <div className="instructions">
          <h3>Next Steps:</h3>
          <ol>
            <li>
              <strong>Click "Copy Token"</strong> above
            </li>
            <li>
              <strong>Go back to VS Code</strong>
            </li>
            <li>
              <strong>Paste the token</strong> when prompted
            </li>
            <li>
              <strong>Start generating flowcharts!</strong>
            </li>
          </ol>
        </div>

        <div className="footer-note">
          <p>
            ⚠️ Keep this token secure! It provides access to your account.
          </p>
          <p>
            Token expires in 1 hour. You can login again anytime.
          </p>
        </div>
      </div>

      <style jsx>{`
        .vscode-auth-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .auth-container {
          background: white;
          border-radius: 12px;
          padding: 40px;
          max-width: 700px;
          width: 100%;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
        }

        .success-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .success-header h1 {
          color: #333;
          margin-top: 15px;
        }

        .subtitle {
          text-align: center;
          color: #666;
          font-size: 18px;
          margin-bottom: 30px;
        }

        .user-info {
          background: #f5f5f5;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 25px;
        }

        .user-info p {
          margin: 8px 0;
          color: #333;
        }

        .teams-info {
          margin-bottom: 25px;
        }

        .teams-info h3 {
          color: #333;
          margin-bottom: 15px;
        }

        .teams-list {
          list-style: none;
          padding: 0;
        }

        .teams-list li {
          background: #f0f0f0;
          padding: 12px 15px;
          border-radius: 6px;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .role-badge {
          background: #667eea;
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          text-transform: uppercase;
        }

        .no-teams {
          color: #999;
          font-style: italic;
        }

        .token-section {
          margin: 30px 0;
        }

        .token-section h3 {
          color: #333;
          margin-bottom: 15px;
        }

        .token-container {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .token-display {
          width: 100%;
          padding: 15px;
          border: 2px solid #ddd;
          border-radius: 8px;
          font-family: "Courier New", monospace;
          font-size: 12px;
          background: #fafafa;
          resize: none;
          color: #333;
        }

        .copy-button {
          background: #4CAF50;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
        }

        .copy-button:hover:not(:disabled) {
          background: #45a049;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
        }

        .copy-button:disabled {
          background: #9e9e9e;
          cursor: not-allowed;
        }

        .instructions {
          background: #e3f2fd;
          border-radius: 8px;
          padding: 20px;
          margin: 25px 0;
        }

        .instructions h3 {
          color: #1976d2;
          margin-bottom: 15px;
        }

        .instructions ol {
          margin: 0;
          padding-left: 20px;
        }

        .instructions li {
          margin: 10px 0;
          color: #333;
          line-height: 1.6;
        }

        .footer-note {
          text-align: center;
          color: #999;
          font-size: 14px;
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .footer-note p {
          margin: 8px 0;
        }

        .auth-info {
          background: #fff3e0;
          border-radius: 8px;
          padding: 20px;
          margin: 25px 0;
        }

        .auth-info h3 {
          color: #e65100;
          margin-bottom: 15px;
        }

        .auth-info ol {
          margin: 0;
          padding-left: 20px;
        }

        .auth-info li {
          margin: 10px 0;
          color: #333;
        }

        .instruction {
          text-align: center;
          color: #666;
          font-size: 16px;
          margin-top: 30px;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}


